/**
 * The quiz builder's model: its form shape, its draft persistence, its option
 * lists, and the rules that decide whether a question or a step is complete.
 *
 * WHY THIS IS ITS OWN MODULE
 * All of it used to sit at the top of `ManagementPlaceholders.tsx`, above a
 * 2,400-line component, which meant none of it could be read, imported or
 * tested without dragging the whole page in. Splitting the page starts here:
 * the pure model first, the state machine (`useQuizBuilder`) next, the step
 * components last. Nothing in this file may import React or the page.
 *
 * `validateAnswerKey`, `validateOptionSet` and `findIncompleteQuestions` are
 * re-exported from the page so existing imports keep working.
 */
import type { QuestionType, QuizQuestion } from "../../services/api";
import { formatISTDate } from "../../services/dateFormat";

/**
 * Is this question's answer key complete? Returns a message, or null if fine.
 *
 * WHY THIS IS SHARED
 * This rule existed in three places and all three disagreed:
 *   - the ADD form checked it (and refused to add without a key),
 *   - `findIncompleteQuestions` checked it again with a LOOSER numeric test,
 *   - the EDIT form did not check it at all.
 * So a teacher could add a question correctly, edit it, clear the answer, and
 * save the question with no complaint — deferring the error to the moment they
 * pressed Save Quiz, which is exactly the "twenty complaints about questions
 * you can no longer see" failure the add form was written to prevent.
 *
 * Nothing could be corrupted by this: `numeric_answer` is `Optional[float]`
 * server-side, so a blank never becomes a stored answer. The cost was entirely
 * in when the author found out.
 *
 * `numeric_answer` arrives as a STRING from the composer (an in-progress form
 * field) and as a NUMBER from a saved question, so both are accepted here —
 * that difference is the reason the two copies drifted in the first place.
 */
export const validateAnswerKey = (question: {
    question_type?: QuestionType | string;
    correct_option?: number | null;
    correct_options?: number[] | null;
    numeric_answer?: number | string | null;
}): string | null => {
    const type = question.question_type || "mcq_single";

    if (type === "mcq_single" || type === "true_false") {
        return question.correct_option == null ? "Mark which option is the correct answer." : null;
    }
    if (type === "mcq_multi") {
        return question.correct_options?.length ? null : "Tick at least one correct answer.";
    }
    if (type === "integer") {
        // A blank string is as missing as null. The old gate tested only
        // `== null`, so "" slipped past it.
        const answer = question.numeric_answer;
        const missing = answer == null || (typeof answer === "string" && !answer.trim());
        return missing ? "Enter the correct numeric answer." : null;
    }
    // short_answer is free text and never auto-graded, so it has no key to miss.
    return null;
};

/**
 * Whether a set of answer options is usable, or why it is not.
 *
 * AN OPTION CAN BE A PICTURE.
 * The composer has always accepted an image per option — the whole point, for
 * geometry, circuit diagrams, map reading and every "which figure comes next"
 * reasoning question, where the options ARE the diagrams and text would be a
 * description of the answer. Both callers demanded text anyway, so uploading
 * four images and leaving the fields blank was rejected with "remove unused
 * options", which is the opposite of what the author had just done.
 *
 * Uniqueness deliberately applies to WORDS ONLY: two image options both carry
 * an empty string, and comparing those reports "options must be unique" for two
 * entirely different pictures.
 *
 * One function because there were two copies, and they had already drifted into
 * two different messages for the same situation.
 */
export const validateOptionSet = (
    options: string[],
    images: (string | null)[] | null
): string | null => {
    const trimmed = options.map((option) => (option ?? "").trim());
    const hasImage = (index: number) => Boolean(images?.[index]);

    if (trimmed.some((option, index) => !option && !hasImage(index))) {
        return "Each option needs text or an image — remove unused options instead of leaving them blank.";
    }

    const words = trimmed.filter(Boolean).map((option) => option.toLowerCase());
    if (new Set(words).size !== words.length) return "Options must be unique.";

    return null;
};

export const findIncompleteQuestions = (questions: QuizQuestion[]): string[] => {
    const problems: string[] = [];
    questions.forEach((q, index) => {
        const label = `Q${index + 1}`;
        // Same rule as both question forms — this gate used to test `== null`
        // for the numeric case, which let an empty string through.
        if (validateAnswerKey(q)) {
            const type = q.question_type || "mcq_single";
            if (type === "mcq_multi") problems.push(`${label}: no correct options selected`);
            else if (type === "integer") problems.push(`${label}: no numeric answer set`);
            else problems.push(`${label}: no correct option selected`);
        }
    });
    return problems;
};

export type QuizFormState = {
    coaching_id: string;
    title: string;
    exam_id: string;
    subject_ids: string[];
    duration_minutes: number;
    go_live_at: string;
    close_at: string;
    publish_answer_sheet_at: string;
    result_visibility: "scheduled" | "published" | "hidden";
    exam_category: string;
    grade_level: string;
    ai_solutions_enabled: boolean;
    // Applied to uploaded/AI-generated questions only — the manual composer
    // already sets marks per-question. Without this, bulk-imported questions
    // (the CSV template has no marks column) silently landed on the model's
    // hardcoded 1/0 with no way to change them short of editing every row in
    // the preview modal.
    default_marks: number;
    default_negative_marks: number;
};

/** `datetime-local` wants "YYYY-MM-DDTHH:mm" in LOCAL time — an ISO/UTC string
 *  is silently ignored by the control and the field renders blank.
 *
 *  Local time is what the admin means: this platform runs on IST, the whole
 *  product renders IST everywhere else, and a browser on an Indian machine is
 *  already in that zone. The value is converted back to UTC on submit. */
export const toDateTimeLocalValue = (date: Date): string => {
    const pad = (n: number) => String(n).padStart(2, "0");
    return (
        `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}` +
        `T${pad(date.getHours())}:${pad(date.getMinutes())}`
    );
};

/** Round up to the next 15 minutes. A prefilled "09:37" looks like a mistake;
 *  "09:45" reads as a sensible default someone chose. */
export const nextQuarterHour = (from: Date): Date => {
    const rounded = new Date(from);
    rounded.setSeconds(0, 0);
    rounded.setMinutes(Math.ceil((rounded.getMinutes() + 1) / 15) * 15);
    return rounded;
};

export const buildQuizFormState = (coachingId?: string): QuizFormState => {
    // PREFILLED, not blank. Three empty datetime boxes are the slowest part of
    // this form and the least interesting: an admin almost always wants "soon,
    // for a couple of hours, answers after it closes". Defaults make the
    // common case zero typing and still leave every field editable.
    const goLive = nextQuarterHour(new Date());
    const closeAt = new Date(goLive.getTime() + 2 * 60 * 60 * 1000);
    // Equal to close, not after: the answer key is useful the moment the paper
    // shuts, and a later default silently withholds it.
    const publishAt = new Date(closeAt.getTime());
    return {
    coaching_id: coachingId ?? "",
    title: "",
    exam_id: "",
    subject_ids: [],
    duration_minutes: 60,
    go_live_at: toDateTimeLocalValue(goLive),
    close_at: toDateTimeLocalValue(closeAt),
    publish_answer_sheet_at: toDateTimeLocalValue(publishAt),
    result_visibility: "scheduled",
    exam_category: "",
    grade_level: "",
    // Off by default — AI solutions cost tokens and go out under the
    // coaching's name, so an admin opts in per paper.
    ai_solutions_enabled: false,
    default_marks: 1,
    default_negative_marks: 0
    };
};

/** One resumable in-progress quiz draft, saved to the browser between visits
 * to the Create Quiz wizard. Deliberately excludes anything that can't
 * survive JSON (the raw uploaded File, in-progress unsaved composer/AI-spec
 * fields) — only questions actually added to the paper, plus step-1 config,
 * count as "completed" progress worth resuming. */
export type PersistedQuizDraft = {
    currentStep: 1 | 2 | 3;
    formState: QuizFormState;
    ingestionQuestions: QuizQuestion[] | null;
    ingestionWarnings: string[];
    ingestionSourceUri: string | null;
    manualQuestions: QuizQuestion[];
    aiGeneratedIds: string[];
    /** The question BEING WRITTEN, not yet added to the paper.
     *
     * Everything already added survived a refresh; the one in the composer did
     * not, so a phone rotation or a backgrounded tab threw away the question
     * the author was in the middle of — the only one they could see.
     *
     * Optional because drafts written before this field existed do not carry
     * it, and a resume must not break on them.
     *
     * ITS IMAGE IS DELIBERATELY NOT STORED. A diagram is a base64 data URI up
     * to ~533 KB encoded, against a storage budget of about 5 MB, and quota
     * pressure is the very thing that used to freeze this draft. Restoring the
     * text, options and answer key while asking for the diagram again is a far
     * better trade than making every save more likely to fail. */
    inFlightQuestion?: Omit<DraftQuestion, "image_url"> | null;
    /** Which composer tab was open, so a resume lands where they left off. */
    activeMethod?: "upload" | "ai" | "manual" | null;
};

/** Prefix for every stored draft. One key per account lives under it.
 *
 * NAMESPACED PER USER, AND CLEARED ON LOGOUT. BOTH, BECAUSE EITHER ALONE LEAKS.
 *
 * This used to be the whole key: one draft for the browser, no owner. Student A
 * would build a paper, log out, and student B logging in on the same machine
 * got A's draft restored into their builder on mount -- including
 * `manualQuestions`, which carry `correct_option`, `correct_options` and
 * `numeric_answer`. A's private practice paper AND its answer key.
 *
 * A shared device is not an exotic case here; it is what a coaching institute's
 * computer room is.
 *
 * Namespacing alone would stop B READING A's draft but leave it on disk for
 * anyone with devtools. Clearing on logout alone would miss the far commoner
 * case of closing the tab without logging out. Together the data is unreadable
 * by the next account and gone at the first clean exit.
 */
export const QUIZ_DRAFT_KEY_PREFIX = "greenagentick:quiz-draft:v2:";

/** The storage key for one account's draft.
 *
 * v2 because v1 keys are unowned and must not be adopted by whoever opens the
 * builder next -- which is the bug. They are removed by `clearAllQuizDrafts`
 * rather than migrated: a draft whose author cannot be established is not
 * something to hand to the current user.
 */
export const quizDraftKey = (ownerId: string): string => `${QUIZ_DRAFT_KEY_PREFIX}${ownerId}`;

/** @deprecated The unowned v1 key. Exported only so the logout sweep can
 *  remove it from browsers that still carry one. */
export const LEGACY_QUIZ_DRAFT_STORAGE_KEY = "examintel:quiz-draft:v1";

/** @deprecated The v2 prefix under the previous brand name ("examintel"),
 *  before the rename to Green Agentick.
 *
 *  NOT migrated — deliberately. It is swept, exactly like the v1 key above.
 *  The rename would otherwise be a silent security regression: a draft carries
 *  `correct_option` / `correct_options` / `numeric_answer`, and the whole point
 *  of `clearAllQuizDrafts` is that an answer key does not survive on a shared
 *  institute machine after its author signs out. Change the prefix without
 *  sweeping the old one and every draft written before the rename becomes
 *  unreadable by the app but still sitting in localStorage for anyone with
 *  devtools — invisible to the code that is supposed to remove it. */
export const LEGACY_BRAND_QUIZ_DRAFT_KEY_PREFIX = "examintel:quiz-draft:v2:";

export const draftHasContent = (draft: PersistedQuizDraft): boolean =>
    Boolean(draft.formState?.title?.trim()) || draft.manualQuestions.length > 0 || (draft.ingestionQuestions?.length ?? 0) > 0;

export const loadQuizDraft = (ownerId: string | null | undefined): PersistedQuizDraft | null => {
    // No owner, no draft. Reading one while signed out is exactly the state
    // this bug was: a draft with no established author, offered to whoever is
    // at the keyboard.
    if (!ownerId) {
        return null;
    }
    try {
        const raw = localStorage.getItem(quizDraftKey(ownerId));
        return raw ? (JSON.parse(raw) as PersistedQuizDraft) : null;
    } catch {
        return null;
    }
};

/** Writes the draft. Returns false when it could not be stored.
 *
 * THE FAILURE USED TO BE SWALLOWED, AND THAT WAS WORSE THAN LOSING THE WRITE.
 *
 * `setItem` throwing leaves the PREVIOUS value intact. So once quota was hit —
 * diagram images ride along as base64 data URIs up to ~533 KB encoded, and the
 * budget is about 5 MB — every later save failed silently while the stored
 * draft stayed frozen at whatever it held at that moment. The resume banner
 * then offered that stale draft and announced it as complete: "12 questions
 * saved, on step 2". An author who accepted it lost every question added after
 * the quota was hit, and nothing anywhere said so.
 *
 * Two changes. The retry: the stored value is the single biggest thing standing
 * in the way of the new one, and it is superseded by the draft being written,
 * so dropping it and writing again usually succeeds. And the return value, so
 * the caller can say plainly that local saving has stopped — the pattern the
 * exam player already uses for its own autosave.
 *
 * If the retry also fails the key is now ABSENT rather than stale. That is the
 * intended outcome, not collateral damage: no offer at all beats an offer that
 * misrepresents what it holds, and the author is being told in-session.
 */
export const saveQuizDraft = (
    ownerId: string | null | undefined,
    draft: PersistedQuizDraft,
): boolean => {
    if (!ownerId) {
        return false;
    }
    const key = quizDraftKey(ownerId);
    const payload = JSON.stringify(draft);
    try {
        localStorage.setItem(key, payload);
        return true;
    } catch {
        try {
            localStorage.removeItem(key);
            localStorage.setItem(key, payload);
            return true;
        } catch {
            // Genuinely unavailable: quota too small even for this draft alone,
            // or storage disabled entirely (private browsing).
            return false;
        }
    }
};

export const clearQuizDraft = (ownerId: string | null | undefined) => {
    if (!ownerId) {
        return;
    }
    try {
        localStorage.removeItem(quizDraftKey(ownerId));
    } catch {
        // ignore
    }
};

/** Remove every stored draft, whoever owns it. Called on logout.
 *
 * Sweeps by PREFIX rather than removing one key: the machine may hold drafts
 * for several accounts, and the point of logging out on a shared computer is
 * that nothing of yours is left behind. The unowned v1 key goes too.
 */
export const clearAllQuizDrafts = () => {
    try {
        const doomed: string[] = [];
        for (let i = 0; i < localStorage.length; i += 1) {
            const key = localStorage.key(i);
            if (
                key &&
                (key.startsWith(QUIZ_DRAFT_KEY_PREFIX) ||
                    key.startsWith(LEGACY_BRAND_QUIZ_DRAFT_KEY_PREFIX) ||
                    key === LEGACY_QUIZ_DRAFT_STORAGE_KEY)
            ) {
                doomed.push(key);
            }
        }
        // Collected first: removing during the walk shifts the indices and
        // silently skips every other key.
        doomed.forEach((key) => localStorage.removeItem(key));
    } catch {
        // Storage unavailable (private browsing). Nothing was stored either.
    }
};

/**
 * Universal exam taxonomy — covers school (class 1–12), government &
 * competitive, college/university, corporate skill assessments, and
 * professional certifications so any institution can model its papers.
 */
export const EXAM_CATEGORY_OPTIONS = [
    { value: "school", label: "School (Class 1–12)" },
    { value: "competitive", label: "Government / Competitive (UPSC, SSC, JEE, NEET…)" },
    { value: "college", label: "College / University" },
    { value: "corporate", label: "Corporate / Skill assessment" },
    { value: "certification", label: "Professional certification" },
    { value: "custom", label: "Custom / Other" }
];

export const GRADE_OPTIONS = Array.from({ length: 12 }, (_, i) => `Class ${i + 1}`);

export const QUESTION_TYPE_OPTIONS: ReadonlyArray<{ value: QuestionType; label: string }> = [
    { value: "mcq_single", label: "Single correct (MCQ)" },
    { value: "mcq_multi", label: "Multiple correct" },
    { value: "true_false", label: "True / False" },
    { value: "integer", label: "Numeric answer" },
    { value: "short_answer", label: "Short answer (manually graded)" }
];

export type DraftQuestion = {
    text: string;
    question_type: QuestionType;
    options: string[];
    // null = "no answer set yet" — the answer key is optional while authoring;
    // a quiz just can't go live until every question has one (server-enforced).
    correct_option: number | null;
    correct_options: number[];
    numeric_answer: string;
    numeric_tolerance: string;
    model_answer_text: string;
    explanation: string;
    quick_solve_trick: string;
    subject: string;
    marks: number;
    negative_marks: number;
    image_url: string;
    /** Index-aligned with `options`; null where an option is text-only. */
    option_images: (string | null)[] | null;
};

/** What the chat hands over when a student confirms "build me a quiz on X". */
export interface AiPrefill {
    topic: string;
    count?: number;
    difficulty?: string;
    kind?: string;
}

/** Has the author actually put something in the composer?
 *
 * Guards against persisting — and then offering to restore — an untouched blank
 * form, which would make every resume land on an empty composer for no reason.
 * Only the fields a person types are considered; `options: ["","","",""]` and
 * the marks defaults are present from the moment the form mounts. */
export const draftHasBeenStarted = (draft: DraftQuestion): boolean =>
    Boolean(
        draft.text.trim() ||
            draft.options.some((option) => option.trim()) ||
            draft.explanation.trim() ||
            draft.quick_solve_trick.trim() ||
            draft.model_answer_text.trim() ||
            draft.numeric_answer.trim() ||
            draft.image_url
    );

/** The composer draft as it is stored: everything except the diagram. */
export const stripDraftImage = (draft: DraftQuestion): Omit<DraftQuestion, "image_url"> => {
    const { image_url: _discarded, ...rest } = draft;
    return rest;
};

export const buildDraftQuestion = (): DraftQuestion => ({
    text: "",
    question_type: "mcq_single",
    options: ["", "", "", ""],
    correct_option: null,
    correct_options: [],
    numeric_answer: "",
    numeric_tolerance: "",
    model_answer_text: "",
    explanation: "",
    quick_solve_trick: "",
    subject: "",
    marks: 1,
    negative_marks: 0,
    image_url: "",
    option_images: null
});

/** Per-option images are inlined as data URIs and there is one PER OPTION, so
 *  the cap is tighter than the question diagram's: six 400 KB options would put
 *  2.4 MB into a single question document. */

/** Most chips rendered at once in the subject picker. Enough to browse an area
 *  without scrolling past the form, few enough that typing stays responsive. */

/** The three ways to get questions into a paper. Alternatives, not stages —
 *  they all feed the same list, and an author can use more than one. */
export const QUESTION_METHODS: ReadonlyArray<{
    id: "upload" | "ai" | "manual";
    icon: string;
    title: string;
    blurb: string;
}> = [
    {
        id: "manual",
        icon: "✎",
        title: "Write by myself",
        blurb: "Type each question, its options, and mark the correct answer."
    },
    {
        id: "upload",
        icon: "📄",
        title: "By file upload",
        blurb: "Import an existing paper: CSV, XLSX, DOCX, PDF, or a photo of a printed page."
    },
    {
        id: "ai",
        icon: "★",
        title: "By generative AI",
        blurb: "Give a topic and a count — every question is reviewed before it saves."
    }
];

/** Steps in the builder.
 *
 * The subtitles do real work. Read as "Setup / Questions / Preview" the first
 * two look like alternatives — which is exactly how this was misread — because
 * nothing said Setup applies to every path. Naming what each step is FOR makes
 * the sequence legible, and the choice between authoring methods sits inside
 * step 2 where it belongs.
 */
/** The three steps, worded for whoever is building.
 *
 *  A STUDENT here is making a PRIVATE practice paper that reaches nobody else —
 *  that privacy is the whole reason students may author without an approval
 *  queue. Telling them they are about to "publish" says the opposite of what
 *  the server will do, and this page said it in several places at once:
 *  "Manage quizzes", "Create quiz", "downstream audit trails".
 *
 *  The behaviour was always right; only the voice was an administrator's. */
export const quizSteps = (isStudentAuthor: boolean): ReadonlyArray<{ id: 1 | 2 | 3; title: string; subtitle: string }> => [
    { id: 1, title: "Set up the paper", subtitle: "Title, schedule, subjects" },
    { id: 2, title: "Add questions", subtitle: "Write, generate, or upload" },
    isStudentAuthor
        ? { id: 3, title: "Review & save", subtitle: "Check every question" }
        : { id: 3, title: "Review & publish", subtitle: "Check every question" }
];

export const RESULT_VISIBILITY_OPTIONS = [
    {
        value: "scheduled",
        label: "Scheduled release",
        helper: "Results unlock at the configured announcement time."
    },
    {
        value: "published",
        label: "Immediate",
        helper: "Publish the answer sheet as soon as the quiz goes live."
    },
    {
        value: "hidden",
        label: "Manual",
        helper: "Keep results hidden until an admin updates the quiz."
    }
];

/** Mirrors MAX_INGEST_FILES on the server. Checked here too so a student
 *  learns the limit before waiting for a large upload to be rejected. */
export const MAX_UPLOAD_FILES = 60;

/** Mirrors the backend's answer-key completeness gate so the admin sees the
 * same "can't go live" problems before hitting Save, not just after a 400. */
/**
 * Everything wrong with ONE question, in words its author can act on.
 *
 * WHY PER-QUESTION AND NOT A LIST OF STRINGS
 * The old check produced a sentence — "Q1: no correct option selected; Q7: ..."
 * — printed once at the top of Review. It told you a problem existed and left
 * you to find it: on a 40-question paper that means counting cards. Worse, an
 * IMPORTED paper can arrive with several of these at once and the author never
 * wrote the questions, so "Q7" means nothing to them.
 *
 * Returning the issues per question lets the card carry its own problem and a
 * button that opens it, which is the difference between a warning and a fix.
 * One helper serves every source — typed, uploaded and generated — so an
 * imported paper is held to exactly the same bar as a hand-written one.
 */
export const questionIssues = (question: QuizQuestion): string[] => {
    const issues: string[] = [];
    const type = question.question_type || "mcq_single";
    const options = question.options ?? [];
    const needsOptions = type === "mcq_single" || type === "mcq_multi" || type === "true_false";

    if (!question.text?.trim()) {
        issues.push("The question text is empty.");
    }
    if (needsOptions) {
        if (options.length < 2) {
            issues.push("Needs at least two options.");
        }
        if (options.some((option) => !option?.trim())) {
            issues.push("One or more options are blank.");
        }
        const seen = options.map((o) => (o ?? "").trim().toLowerCase()).filter(Boolean);
        if (new Set(seen).size !== seen.length) {
            issues.push("Two options are identical.");
        }
    }
    // The answer key. This is the one that blocked saving, and the one an
    // import most often lacks: a spreadsheet with no answer column parses
    // perfectly and produces a paper that cannot go live.
    if ((type === "mcq_single" || type === "true_false") && question.correct_option == null) {
        issues.push("No correct answer marked.");
    }
    if (type === "mcq_multi" && !question.correct_options?.length) {
        issues.push("No correct answers ticked.");
    }
    if (type === "integer" && question.numeric_answer == null) {
        issues.push("No numeric answer set.");
    }
    if (
        (type === "mcq_single" || type === "true_false") &&
        question.correct_option != null &&
        question.correct_option >= options.length
    ) {
        issues.push("The marked answer points past the last option.");
    }
    return issues;
};

/** How many questions one AI call asks for.
 *
 * MEASURED, not guessed. On the local CPU model a call costs ~33s of fixed
 * overhead plus ~36.5s per question (1 question: 69.6s; 3 questions: 142.6s).
 * So asking for all 10 at once is the fastest possible TOTAL — and the worst
 * possible experience, because it is 6.6 minutes with nothing on screen and no
 * way to stop. Batching costs roughly 25% more wall-clock and pays for it by
 * putting the first questions on screen in ~2 minutes, letting the author read
 * them while the rest are written, and letting them stop when they have enough.
 */
export const AI_BATCH_SIZE = 3;

/** Measured cost of one generation call on the local CPU model, in seconds.
 *
 *  Fitted from three runs against the running server on 12-Sep-2026:
 *  1 question 69.6s, 3 questions 142.6s, 6 questions 234.1s. That is ~33s per
 *  question plus ~37s of fixed cost per CALL — which is exactly why batches
 *  cost more in total, and why the estimate below counts the batches.
 */
export const AI_SECONDS_PER_QUESTION = 33;
export const AI_SECONDS_PER_CALL = 37;

/** The sizes of the calls a request for `total` questions becomes. */
export const planGenerationBatches = (total: number, size: number = AI_BATCH_SIZE): number[] => {
    const wanted = Math.max(0, Math.floor(total) || 0);
    const step = Math.max(1, Math.floor(size) || 1);
    const batches: number[] = [];
    for (let start = 0; start < wanted; start += step) {
        batches.push(Math.min(step, wanted - start));
    }
    return batches;
};

/** How long that plan will really take, batching included.
 *
 *  Quoted to the author BEFORE they start, because the honest number is minutes
 *  and the old screen said nothing at all.
 */
export const estimateGenerationSeconds = (total: number, size: number = AI_BATCH_SIZE): number => {
    const batches = planGenerationBatches(total, size);
    const questions = batches.reduce((sum, batch) => sum + batch, 0);
    return batches.length * AI_SECONDS_PER_CALL + questions * AI_SECONDS_PER_QUESTION;
};

/** Two questions are "the same" if their text matches once case, punctuation
 *  and spacing are ignored.
 *
 *  Needed BECAUSE of batching: separate calls do not know what the previous
 *  call produced, so the model repeats itself across batches. Without this,
 *  making generation feel faster would have quietly made the papers worse.
 */
export const normaliseQuestionText = (text: string | undefined | null): string =>
    (text ?? "")
        .toLowerCase()
        .replace(/[^a-z0-9\s]/g, " ")
        .replace(/\s+/g, " ")
        .trim();

/** Split `incoming` into the questions not already present.
 *
 *  `seen` is updated in place so a caller can carry it across batches, which is
 *  the whole point: separate AI calls cannot see each other's output.
 */
export const dropDuplicateQuestions = <T extends { text?: string }>(
    seen: Set<string>,
    incoming: T[]
): { fresh: T[]; duplicates: number } => {
    const fresh: T[] = [];
    let duplicates = 0;
    for (const item of incoming) {
        const key = normaliseQuestionText(item.text);
        if (!key || seen.has(key)) {
            duplicates += 1;
            continue;
        }
        seen.add(key);
        fresh.push(item);
    }
    return { fresh, duplicates };
};

export const describeQuestionAnswer = (question: QuizQuestion): string => {
    const type = question.question_type || "mcq_single";
    if (type === "mcq_multi") {
        return question.correct_options?.length
            ? `${question.correct_options.length} correct option${question.correct_options.length === 1 ? "" : "s"}`
            : "answer not set";
    }
    if (type === "integer") {
        return question.numeric_answer != null ? `answer ${question.numeric_answer}` : "answer not set";
    }
    if (type === "short_answer") {
        return "manually graded";
    }
    return question.correct_option != null ? `correct ${String.fromCharCode(65 + question.correct_option)}` : "answer not set";
};

export const formatWindow = (start?: string, end?: string) => {
    if (!start && !end) {
        return "—";
    }
    const startLabel = start ? formatISTDate(start) : "TBD";
    const endLabel = end ? formatISTDate(end) : "TBD";
    return `${startLabel} → ${endLabel}`;
};
